import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Truck, Eye, RotateCcw, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDate } from "@/lib/api";
import { PageHeader, Pager, EmptyState, StatusBadge, DemoBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["pending", "confirmed", "processing", "packed", "ready_to_ship", "shipped", "out_for_delivery", "delivered", "cancelled"];

export default function OrdersPage({ scope = "admin" }) {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [channel, setChannel] = useState("");
  const [detail, setDetail] = useState(null);
  const [returnReason, setReturnReason] = useState("");
  const [returnOpen, setReturnOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["orders", scope, page, search, status, channel],
    queryFn: async () => (await api.get(`/orders?page=${page}&limit=15&search=${encodeURIComponent(search)}&status=${status}&channel=${channel}`)).data,
  });
  const { data: detailData, refetch: refetchDetail } = useQuery({
    queryKey: ["order", detail?.id],
    queryFn: async () => (await api.get(`/orders/${detail.id}`)).data,
    enabled: !!detail,
  });

  const ship = useMutation({
    mutationFn: async (id) => (await api.post(`/orders/${id}/ship`, {})).data,
    onSuccess: (s) => {
      toast.success(`Shipped via IndiaFast · AWB ${s.awb}${scope === "admin" && s.mode === "demo" ? " (demo)" : ""}`);
      qc.invalidateQueries({ queryKey: ["orders"] });
      refetchDetail();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const setOrderStatus = useMutation({
    mutationFn: async ({ id, status }) => api.put(`/orders/${id}/status`, { status }),
    onSuccess: () => { toast.success("Order updated"); qc.invalidateQueries({ queryKey: ["orders"] }); refetchDetail(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const createReturn = useMutation({
    mutationFn: async () => api.post("/returns", { order_id: detail.id, reason: returnReason || "Customer requested return" }),
    onSuccess: () => { toast.success("Return created"); setReturnOpen(false); setReturnReason(""); qc.invalidateQueries({ queryKey: ["orders"] }); refetchDetail(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const deleteOrder = useMutation({
    mutationFn: async (id) => api.delete(`/orders/${id}`),
    onSuccess: () => { toast.success("Order deleted"); setDetail(null); qc.invalidateQueries({ queryKey: ["orders"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const o = detailData || detail;

  return (
    <div data-testid={`${scope}-orders-page`}>
      <PageHeader title="Orders" sub="Unified orders across all channels." testid="orders-title" />

      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative min-w-52 flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="order-search-input" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search order no…" className="border-slate-700 bg-[#111827] pl-9 text-slate-100 placeholder:text-slate-600" />
        </div>
        <Select value={channel} onValueChange={(v) => { setChannel(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="order-channel-filter" className="w-44 border-slate-700 bg-[#111827] text-slate-200"><SelectValue placeholder="All channels" /></SelectTrigger>
          <SelectContent className="border-slate-700 bg-[#111827] text-slate-200">
            <SelectItem value="all">All channels</SelectItem>
            <SelectItem value="store">My Store</SelectItem>
            <SelectItem value="amazon">Amazon</SelectItem>
            <SelectItem value="flipkart">Flipkart</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="order-status-filter" className="w-44 border-slate-700 bg-[#111827] text-slate-200"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent className="border-slate-700 bg-[#111827] text-slate-200">
            <SelectItem value="all">All statuses</SelectItem>
            {[...STATUSES, "return_requested", "returned", "refunded", "rto"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <p className="text-sm text-slate-500">Loading…</p> : !data?.items?.length ? <EmptyState title="No orders yet" sub={scope === "agent" ? "Orders from your store will appear here." : "Customer orders across agents appear here."} /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-800 bg-[#111827]">
          <table className="w-full text-left text-xs" data-testid="orders-table">
            <thead>
              <tr className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Channel</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">AWB</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((order) => (
                <tr key={order.id} data-testid={`order-row-${order.order_no}`} className="border-b border-slate-800/50 hover:bg-slate-900/40">
                  <td className="px-4 py-3">
                    <p className="font-mono font-medium text-slate-200">{order.order_no}</p>
                    <p className="text-[10px] text-slate-500">{fmtDate(order.created_at)}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-400">{order.customer?.name}<br /><span className="font-mono text-[10px]">{order.customer?.mobile}</span></td>
                  <td className="px-4 py-3 capitalize text-slate-400">{order.channel === "store" ? "My Store" : order.channel?.replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 font-medium text-slate-200">{fmtINR(order.total)}</td>
                  <td className="px-4 py-3"><StatusBadge status={order.payment_status} /></td>
                  <td className="px-4 py-3"><StatusBadge status={order.status} /></td>
                  <td className="px-4 py-3 font-mono text-[10px] text-slate-400">{order.awb || "—"}</td>
                  <td className="px-4 py-3">
                    <Button data-testid={`view-order-${order.order_no}`} size="sm" variant="outline" onClick={() => setDetail(order)}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"><Eye size={12} /> View</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pager page={data.page} pages={data.pages} onPage={setPage} />}

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl border-slate-800 bg-[#111827] text-slate-100">
          {o && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <span className="font-mono">{o.order_no}</span>
                  <StatusBadge status={o.status} />
                  {scope === "admin" && o.shipment?.mode === "demo" && <DemoBadge />}
                </DialogTitle>
              </DialogHeader>
              <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1 text-xs">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
                    <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Customer</p>
                    <p className="text-slate-200">{o.customer?.name}</p>
                    <p className="font-mono text-[10px] text-slate-400">{o.customer?.mobile}</p>
                    <p className="mt-1 text-slate-400">{o.customer?.address}, {o.customer?.city}, {o.customer?.state} {o.customer?.pincode}</p>
                  </div>
                  <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
                    <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Financials</p>
                    <div className="space-y-0.5 text-slate-300">
                      <p>Subtotal: {fmtINR(o.subtotal)}</p>
                      <p>Shipping: {fmtINR(o.shipping_cost)}{o.cod_fee ? ` · COD fee: ${fmtINR(o.cod_fee)}` : ""}</p>
                      <p className="font-semibold text-slate-100">Total: {fmtINR(o.total)}</p>
                      <p className="text-emerald-400">Est. profit: {fmtINR(o.estimated_profit)}{o.actual_profit != null && ` · Actual: ${fmtINR(o.actual_profit)}`}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-[10px] uppercase tracking-wider text-slate-500">Items</p>
                  {o.items?.map((it) => (
                    <div key={it.product_id} className="mb-1.5 flex items-center justify-between rounded-lg bg-slate-900/60 px-3 py-2">
                      <div className="flex items-center gap-2.5">
                        {it.image && <img src={it.image} alt="" className="h-8 w-8 rounded object-cover" />}
                        <div>
                          <p className="text-slate-200">{it.name}</p>
                          <p className="font-mono text-[10px] text-slate-500">{it.sku} × {it.qty}</p>
                        </div>
                      </div>
                      <p className="text-slate-300">{fmtINR(it.price * it.qty)}</p>
                    </div>
                  ))}
                </div>
                {o.shipment && (
                  <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
                    <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Shipment</p>
                    <p className="font-mono text-slate-200">{o.shipment.awb} · {o.shipment.courier} {o.shipment.service}</p>
                    <p className="text-slate-400">Status: <StatusBadge status={o.shipment.status} /></p>
                    <div className="mt-2 space-y-1">
                      {(o.shipment.events || []).map((e, i) => (
                        <p key={i} className="text-[10px] text-slate-500">• {e.label} — {fmtDate(e.timestamp)}</p>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button data-testid="customer-invoice-btn" size="sm" variant="outline"
                    onClick={() => window.open(`/invoice/${o.id}/customer`, "_blank")}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    <FileText size={12} /> Customer invoice
                  </Button>
                  <Button data-testid="agent-invoice-btn" size="sm" variant="outline"
                    onClick={() => window.open(`/invoice/${o.id}/agent`, "_blank")}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    <FileText size={12} /> Agent invoice
                  </Button>
                  {scope === "admin" && !["shipped", "out_for_delivery", "delivered", "cancelled", "returned", "refunded", "rto"].includes(o.status) && (
                    <Button data-testid="ship-order-btn" size="sm" onClick={() => ship.mutate(o.id)} disabled={ship.isPending}
                      className="bg-orange-500 text-white hover:bg-orange-600">
                      <Truck size={13} /> {ship.isPending ? "Creating…" : "Ship via IndiaFast"}
                    </Button>
                  )}
                  {scope === "admin" && (
                  <Select value="" onValueChange={(v) => setOrderStatus.mutate({ id: o.id, status: v })}>
                    <SelectTrigger data-testid="order-status-select" className="h-8 w-40 border-slate-700 bg-slate-900 text-xs text-slate-200">
                      <SelectValue placeholder="Update status" />
                    </SelectTrigger>
                    <SelectContent className="border-slate-700 bg-slate-900 text-slate-200">
                      {STATUSES.filter((s) => s !== o.status).map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  )}
                  {scope === "admin" && (
                    <Button data-testid="delete-order-btn" size="sm" variant="outline"
                      onClick={() => { if (window.confirm(`Permanently delete order ${o.order_no}? Payments, shipments and returns are removed; financial ledger history is preserved.`)) deleteOrder.mutate(o.id); }}
                      disabled={deleteOrder.isPending}
                      className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10">
                      <Trash2 size={13} /> Delete
                    </Button>
                  )}
                  {scope === "agent" && (
                    <p data-testid="agent-fulfillment-note" className="w-full rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2 text-[10px] text-slate-500">
                      Shipping & fulfillment are handled by the IndiaFast admin team — you can track every step here.
                    </p>
                  )}
                  {o.status === "delivered" && (
                    <Button data-testid="create-return-btn" size="sm" variant="outline" onClick={() => setReturnOpen(true)}
                      className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10">
                      <RotateCcw size={13} /> Create return
                    </Button>
                  )}
                </div>
                {returnOpen && (
                  <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3">
                    <Input data-testid="return-reason-input" value={returnReason} onChange={(e) => setReturnReason(e.target.value)}
                      placeholder="Return reason…" className="mb-2 border-slate-700 bg-slate-900 text-slate-100" />
                    <Button data-testid="confirm-return-btn" size="sm" onClick={() => createReturn.mutate()} disabled={createReturn.isPending}
                      className="bg-rose-500 text-white hover:bg-rose-600">Confirm return</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
